import { ref, computed, reactive } from 'vue'
import { fetchVehicleFieldMappingConfig, saveFieldMappingDraft } from '@/services/fieldMappingService'
import { IApp, InventorySources, type Models, type UI } from '@/types'
import { defineStore } from 'pinia'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import type {
  IVehicleFieldMapping,
  IVehicleFieldMappingFieldItem,
} from '@/types/vehicleFieldMapping'
import { fetchISEMappingTemplates } from '@/services'
import type { FieldMappingConfig } from '@/types/appTypes'

export const useVehicleFieldMappingStore = defineStore('vehicleFieldMapping', () => {
  const mappingType = ref('Vehicle Availability')
  const segmentation = ref<string>('')
  const segmentationOptions = ref<string[]>([])
  const feedOptions = ref<any>({})
  const fieldOptions = ref<Models.Field[]>([])
  const fieldDefinitions = ref<Record<string, any> | undefined>()
  const fieldNames = ref<string[]>([])
  const originalConfig = ref<FieldMappingConfig | null>(null)
  const locallyStoredConfig = ref<IVehicleFieldMappingFieldItem[] | null>(null)
  const usedDefaultFieldMapping = ref<boolean>(false)

  const mappingTemplates = ref<IISEFieldMapGetTemplateResponse | null>(null)

  const accountSettings = useAccountSettingsStore()
  const appConfig = useJazelAppConfig()
  const vehicleFieldMappingConfig = ref<IVehicleFieldMapping | null>(null)

  const loadingStatus = reactive<{
    initialData: IApp.ILoadingStatus
    fieldFeedOptions: IApp.ILoadingStatus
    saveFieldMapping: IApp.ILoadingStatus
  }>({
    initialData: IApp.ILoadingStatus.Idle,
    fieldFeedOptions: IApp.ILoadingStatus.Idle,
    saveFieldMapping: IApp.ILoadingStatus.Idle,
  })

  const fieldMappings = ref<Models.VehicleField[]>([])
  const remoteFieldMappings = ref<Models.VehicleField[]>([])

  const setVehicleFieldMappingConfig = (value: IVehicleFieldMapping) => {
    vehicleFieldMappingConfig.value = value
  }

  const setSegmentation = (value: string) => {
    segmentation.value = value
  }

  const ensureDefaultConfig = (
    config: IISEFieldMapGetConfigResponse | null,
  ): IISEFieldMapGetConfigResponse => {
    // Deep clone or start fresh
    const cfg = config ? JSON.parse(JSON.stringify(config)) : ({} as IISEFieldMapGetConfigResponse)

    // Default the Enable flag
    cfg.Enable = cfg.Enable ?? false
    cfg.isActive = cfg.isActive ?? false

    // Ensure mapping_config exists
    if (!cfg.mapping_config) {
      cfg.mapping_config = {}
    }

    // Backend stores segments under mapping_config: { mapping_config: { all: {...}, new: {...}, used: {...}, cpo: {...} } }
    // Check if we have at least the 'all' segment with proper structure and non-empty mappings
    const allMappings = cfg.mapping_config.all?.field_mapping?.mappings
    const hasValidSegment = Array.isArray(allMappings) && allMappings.length > 0

    if (!hasValidSegment) {
      usedDefaultFieldMapping.value = true
      // Source recommendations are account-aware and owned by the backend field
      // catalog. Keep the local snapshot empty until those recommendations are
      // explicitly applied.
      if (!cfg.mapping_config.all) {
        cfg.mapping_config.all = {
          field_mapping: {
            mappings: [],
          },
        }
      }

      // Add empty segments for new, used, cpo if they don't exist under mapping_config
      if (!cfg.mapping_config.new) {
        cfg.mapping_config.new = { field_mapping: { mappings: [] } }
      }
      if (!cfg.mapping_config.used) {
        cfg.mapping_config.used = { field_mapping: { mappings: [] } }
      }
      if (!cfg.mapping_config.cpo) {
        cfg.mapping_config.cpo = { field_mapping: { mappings: [] } }
      }
    } else {
      usedDefaultFieldMapping.value = false
    }

    return cfg
  }

  const getMappingTemplates = async () => {
    const endpoint = appConfig.jamsBackendUrl
    const productId = 'ise_srp_import_mapping'
    const token = accountSettings.sessionToken

    mappingTemplates.value = await fetchISEMappingTemplates(endpoint, productId, token)
    return mappingTemplates.value
  }

  const getMappingConfig = async () => {
    const endpoint = appConfig.jamsBackendUrl
    const accountId = accountSettings.accountId
    const token = accountSettings.sessionToken

    const config = await fetchVehicleFieldMappingConfig(endpoint, accountId, token)
    const ensuredConfig = ensureDefaultConfig(config)

    // Make a backup of the original config for comparison when saving
    originalConfig.value = ensuredConfig as unknown as FieldMappingConfig

    return ensuredConfig
  }

  const fetchInitialData = async () => {
    // This function is currently not used but kept for potential future template fetching
    return {}
  }

  const primarySource = ref<InventorySources.SourceType>('homenet')
  const fallbackBehavior = ref('allow-matching')
  const viewMode = ref('all')
  const search = ref('')

  const exceptionRules = ref<Models.ExceptionRule[]>([
    {
      delete: true,
      source: 'fdds_v3',
      condition: 'status',
      operator: 'contains',
      value: 'in-transit',
      action: 'List',
      logicalOperator: 'AND',
    },
    {
      delete: true,
      source: 'fdds_v3',
      condition: 'status',
      operator: 'contains',
      value: 'in-production',
      action: 'List',
      logicalOperator: 'AND',
    },
  ])

  const vehicles = ref<Models.Vehicle[]>([
    {
      selected: false,
      vin: 'WAUJ8GFFXH1000123',
      stock: 'PF45667',
      status: 'Ready for sale',
      sources: {
        homenet: true,
        fdds: true,
        vauto: false,
      },
      listed: true,
    },
    {
      selected: false,
      vin: 'WAUJ8GFFXH1000124',
      stock: 'PF45668',
      status: 'Ready for sale',
      sources: {
        homenet: true,
        fdds: true,
        vauto: true,
      },
      listed: true,
    },
    {
      selected: false,
      vin: 'WAUJ8GFFXH1000125',
      stock: 'PF45669',
      status: 'In Transit',
      sources: {
        homenet: false,
        fdds: true,
        vauto: false,
      },
      listed: true,
    },
    {
      selected: false,
      vin: 'WAUJ8GFFXH1000126',
      stock: 'PF45670',
      status: 'In Transit',
      sources: {
        homenet: false,
        fdds: true,
        vauto: false,
      },
      listed: true,
    },
    {
      selected: false,
      vin: 'WAUJ8GFFXH1000127',
      stock: 'PF45671',
      status: 'In Production',
      sources: {
        homenet: false,
        fdds: true,
        vauto: true,
      },
      listed: false,
    },
    {
      selected: false,
      vin: 'WAUJ8GFFXH1000128',
      stock: 'PF45672',
      status: 'Ready for sale',
      sources: {
        homenet: true,
        fdds: false,
        vauto: true,
      },
      listed: true,
    },
  ])

  const filteredVehicles = computed(() => {
    let filtered = [...vehicles.value]

    if (search.value) {
      const lowerSearch = search.value.toLowerCase()
      filtered = filtered.filter(
        (v) =>
          v.vin.toLowerCase().includes(lowerSearch) ||
          v.stock.toLowerCase().includes(lowerSearch) ||
          v.status.toLowerCase().includes(lowerSearch),
      )
    }

    if (viewMode.value === 'listed') {
      filtered = filtered.filter((v) => v.listed)
    } else if (viewMode.value === 'unlisted') {
      filtered = filtered.filter((v) => !v.listed)
    }

    return filtered
  })

  const ruleHeaders: UI.TableHeader[] = [
    { title: 'Delete', key: 'delete', sortable: false, width: '50px' },
    { title: 'Source', key: 'source', sortable: true },
    { title: 'Condition', key: 'condition', sortable: true },
    { title: 'Operator', key: 'operator', sortable: true },
    { title: 'Value', key: 'value', sortable: false },
    { title: 'Action', key: 'action', sortable: false },
    { title: '', key: 'logicalOperator', sortable: false },
  ]

  const vehicleHeaders: UI.TableHeader[] = [
    { title: '', key: 'selected', sortable: false },
    { title: 'VIN', key: 'vin', sortable: true },
    { title: 'STOCK', key: 'stock', sortable: true },
    { title: 'STATUS', key: 'status', sortable: true },
    {
      title: 'Sources',
      key: 'sources',
      children: [
        { title: 'Homenet', key: 'homenet', sortable: false, align: 'center' },
        { title: 'FDDS/Locate', key: 'fdds', sortable: false, align: 'center' },
        { title: 'Vauto', key: 'vauto', sortable: false, align: 'center' },
      ],
      sortable: false,
      align: 'center',
    },
    { title: 'Active', key: 'listed', sortable: true },
    // { title: 'Actions', key: 'actions', sortable: false, align: 'center' },
  ]

  function moveFieldUp(index: number) {
    if (index > 0) {
      const temp = fieldMappings.value[index]
      fieldMappings.value[index] = fieldMappings.value[index - 1]
      fieldMappings.value[index - 1] = temp
    }
  }

  function moveFieldDown(index: number) {
    if (index < fieldMappings.value.length - 1) {
      const temp = fieldMappings.value[index]
      fieldMappings.value[index] = fieldMappings.value[index + 1]
      fieldMappings.value[index + 1] = temp
    }
  }

  function addExceptionRule() {
    exceptionRules.value.push({
      delete: true,
      source: 'homenet',
      condition: 'status',
      operator: 'contains',
      value: '',
      action: 'List',
      logicalOperator: 'AND',
    })
  }

  function removeRule(index: number) {
    exceptionRules.value.splice(index, 1)
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'Ready for sale':
        return 'success'
      case 'In Transit':
        return 'warning'
      case 'In Production':
        return 'info'
      default:
        return 'grey'
    }
  }

  const prepareDataToSave = (
    origConfig: FieldMappingConfig,
    newConfig: IVehicleFieldMapping,
  ): FieldMappingConfig => {
    const normalizedConfig: FieldMappingConfig = JSON.parse(JSON.stringify(origConfig))
    normalizedConfig.mapping_config_selection = prepareDraftSelections(newConfig)
    return normalizedConfig
  }

  const prepareDraftSelections = (newConfig: IVehicleFieldMapping): Record<string, string | string[]> => {
    const allFields = newConfig.all || []
    return allFields.reduce((acc: Record<string, string | string[]>, field: any) => {
      const source = String(field?.values?.source || '').trim()
      if (!field?.name || !source) return acc

      const fallbackSources = Array.isArray(field?.values?.fallback)
        ? field.values.fallback
            .map((fallback: any) => String(fallback?.source || '').trim())
            .filter(Boolean)
        : []

      acc[field.name] = fallbackSources.length > 0 ? [source, ...fallbackSources] : source
      return acc
    }, {})
  }

  async function saveFieldMapping(newConfig: IVehicleFieldMapping) {
    loadingStatus.saveFieldMapping = IApp.ILoadingStatus.Loading

    // normalize new config for saving
    const payload = prepareDataToSave(originalConfig.value as FieldMappingConfig, newConfig)

    locallyStoredConfig.value = payload as unknown as IVehicleFieldMappingFieldItem[]

    const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
    return await saveFieldMappingDraft(
      iseEndpoint,
      accountSettings.accountId,
      prepareDraftSelections(newConfig),
      accountSettings.sessionToken,
    )
  }

  return {
    mappingType,
    segmentation,
    segmentationOptions,
    feedOptions,
    fieldDefinitions,
    fieldMappings,
    remoteFieldMappings,
    loadingStatus,
    fieldNames,
    fieldOptions,
    moveFieldUp,
    moveFieldDown,
    fetchInitialData,
    setSegmentation,
    getMappingTemplates,
    getMappingConfig,
    saveFieldMapping,
    setVehicleFieldMappingConfig,
    usedDefaultFieldMapping,

    primarySource,
    fallbackBehavior,
    viewMode,
    search,
    exceptionRules,
    ruleHeaders,
    vehicles,
    vehicleHeaders,
    filteredVehicles,
    addExceptionRule,
    removeRule,
    getStatusColor,
    vehicleFieldMappingConfig,
    originalConfig,
    mappingTemplates,
  }
})
